import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

interface Option {
  id: string;
  name: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  function toggle(id: string) {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  }

  const hasSelection = selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5",
            "rounded-md border text-sm font-medium",
            "transition-colors",
            hasSelection
              ? "border-[color-mix(in_srgb,var(--color-accent)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-accent)_10%,transparent)] text-[color:var(--color-accent)]"
              : "border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-md)] hover:bg-[color:var(--color-elevated)]",
          )}
        >
          {label}
          {hasSelection && (
            <span className="rounded-full bg-current/20 px-1.5 py-0.5 text-xs font-semibold leading-none tabular-nums">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-1" align="start" sideOffset={6}>
        {options.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-[color:var(--color-text-lo)]">
            No options
          </p>
        ) : (
          <ul role="list" className="flex flex-col gap-0.5">
            {options.map((opt) => {
              const isSelected = selected.includes(opt.id);
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => toggle(opt.id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded px-2 py-1.5",
                      "text-sm text-[color:var(--color-text-hi)]",
                      "hover:bg-[color:var(--color-elevated)]",
                      "transition-colors",
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      aria-hidden="true"
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                    {opt.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
